import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import VintageLayout from '../layout/VintageLayout';
import { audioManager } from '../../utils/AudioManager';

interface SetupMemoriesProps {
    onSubmit: (souvenirs: { text: string; answer: string; prompt: string; cells: number[] }[], shadowZones: number[]) => void;
    isWaiting: boolean;
    // Pack memories now include text AND response
    packMemories?: { text: string; response: string }[];
    redThread?: string; // [NEW] For {fil_rouge} replacement
}

// Helper to determine size based on word count
const getMemorySize = (text: string): number => {
    // V3 Rule: Size = Word Count (Clamped between 2 and 5)
    // "1789" -> 1 word -> Size 2 (Min)
    // "Prise de la Bastille" -> 4 words -> Size 4
    const wordCount = text.split(/\s+/).length;
    return Math.max(2, Math.min(5, wordCount));
};

const SetupMemories: React.FC<SetupMemoriesProps> = ({ onSubmit, isWaiting, packMemories = [], redThread = "Mes Aventures" }) => {
    // --- STATE ---
    const [step, setStep] = useState<'SELECT' | 'PLACE' | 'SHADOWS'>('SELECT');

    // Step 1: Selection
    // Store full object: { "Question Text": "Answer" }
    const [selectedMemories, setSelectedMemories] = useState<Record<string, string>>({});

    // Step 2: Placement
    const [placedShapes, setPlacedShapes] = useState<Record<string, number[]>>({}); // memoryText -> cells[]
    const [currentMemoryToPlace, setCurrentMemoryToPlace] = useState<string | null>(null);
    const [rotation, setRotation] = useState<'H' | 'V' | 'D1' | 'D2'>('H'); // H, V, D1 (Diag Down-Right), D2 (Diag Up-Right)
    const [hoverCells, setHoverCells] = useState<number[] | null>(null);

    // Step 3: Shadows
    const [shadowZones, setShadowZones] = useState<number[]>([]);

    // UI Helpers
    const [hoverValid, setHoverValid] = useState(true); // Tracks if current hover placement is OOB

    const memoriesList = Object.keys(selectedMemories);

    // [NEW] Template Replacement Helper
    const formatTemplate = (text: string) => {
        return text.replace(/{fil_rouge}/g, redThread);
    };

    // --- LOGIC: SELECTION ---
    // --- LOGIC: SELECTION ---
    const toggleMemorySelection = (memoryText: string, memoryResponse: string) => {
        audioManager.playSFX('click');
        if (memoryText in selectedMemories) {
            const newMemories = { ...selectedMemories };
            delete newMemories[memoryText];
            setSelectedMemories(newMemories);
        } else {
            if (Object.keys(selectedMemories).length < 3) {
                // Store the PREDEFINED response as the answer. No editing allowed.
                setSelectedMemories({ ...selectedMemories, [memoryText]: memoryResponse });
            }
        }
    };

    const updateMemoryAnswer = (memory: string, answer: string) => {
        if (memory in selectedMemories) {
            setSelectedMemories({ ...selectedMemories, [memory]: answer });
        }
    };

    // --- LOGIC: PLACEMENT ---
    // --- LOGIC: PLACEMENT ---
    const getShapeCells = (startIndex: number, size: number, rot: 'H' | 'V' | 'D1' | 'D2'): { cells: number[], isValid: boolean } => {
        const cells: number[] = [];
        let isValid = true;
        const r = Math.floor(startIndex / 10);
        const c = startIndex % 10;

        for (let i = 0; i < size; i++) {
            let idx = -1;
            let checkBoundary = true;

            if (rot === 'H') {
                if (c + i >= 10) { isValid = false; checkBoundary = false; } // Horizontal spill
                idx = startIndex + i;
            } else if (rot === 'V') {
                if (r + i >= 10) { isValid = false; checkBoundary = false; } // Vertical spill
                idx = startIndex + i * 10;
            } else if (rot === 'D1') { // Diag Down-Right ( \ )
                if (c + i >= 10 || r + i >= 10) { isValid = false; checkBoundary = false; }
                idx = startIndex + (i * 11);
            } else if (rot === 'D2') { // Diag Up-Right ( / )
                if (c + i >= 10 || r - i < 0) { isValid = false; checkBoundary = false; }
                idx = startIndex - (i * 9);
            }

            // Only add to result if it is within 0-99 and visually coherent (approx check)
            // Ideally we show the part that fits.
            if (idx >= 0 && idx < 100) {
                // For H/V/Diag, if we spilled over visually (wrapping lines), it's invalid logic but index might exist
                // e.g. H at col 9 + 1 = col 0 next row? No, 9+1=10.
                // My boundary checks above handle logic validity. 
                // Here we just check grid existence for rendering.
                cells.push(idx);
            }
        }
        return { cells, isValid };
    };

    const isCellOccupied = (idx: number, excludeMemory?: string) => {
        for (const [mem, cells] of Object.entries(placedShapes)) {
            if (mem === excludeMemory) continue;
            if (cells.includes(idx)) return true;
        }
        return false;
    };

    const handleGridHover = (idx: number) => {
        if (!currentMemoryToPlace) {
            setHoverCells(null);
            return;
        }

        const size = getMemorySize(currentMemoryToPlace);
        const { cells, isValid } = getShapeCells(idx, size, rotation);

        // We set hover cells even if invalid, effectively showing the "Out of Bounds" state
        // We'll mark them as collision in the render loop if isValid is false
        // But to pass state cleanly, let's attach metadata? 
        // Simpler: Just set cells. The render loop checks collision. 
        // But we need to know if it was *structurally* invalid (OOB).
        // Let's cheat: we set hoverCells to the cells, 
        // and we rely on the fact that if it was OOB, the cells list is truncated or the specialized collision check below fails?
        // Actually, we need to pass the 'isValid' state to render.
        // Let's store [cells] in state, but we also need a 'isHoverValid' state?
        // Alternative: If OOB, we simply don't set hoverCells? NO, that causes invisible bug.
        // Solution: setHoverCells(cells). Render will show them.
        // But how do we insure they are RED?
        // We can check validity inside render loop again? Or store it.
        // Let's verify 'isCellOccupied' logic in render.
        // If I return truncated cells, the user sees a cut-off shape. It should be RED.

        // Hack for clean component state:
        // We will store { cells: number[], isOOB: boolean } in hover state?
        // Too complex refactor.
        // Let's just make sure 'handleGridClick' double checks validity.
        // And for 'render', if logic says OOB, we want RED.
        // Let's keep hoverCells as number[].
        // We re-run getShapeCells in render? No, expensive.
        // We can stick a magic number? No.

        // Let's use a ref or simple state addition.
        setHoverCells(cells);
        setHoverValid(isValid); // New State
    };

    // NEW STATE for OOB tracking - REMOVED DUPLICATE

    const handleGridClick = (idx: number) => {
        if (step === 'PLACE' && currentMemoryToPlace && hoverCells) {
            // Re-validate everything just to be safe
            const size = getMemorySize(currentMemoryToPlace);
            const { isValid: isShapeValid } = getShapeCells(idx, size, rotation);
            const hasCollision = hoverCells.some(c => isCellOccupied(c));

            if (isShapeValid && !hasCollision) {
                setPlacedShapes({ ...placedShapes, [currentMemoryToPlace]: hoverCells });
                setCurrentMemoryToPlace(null);
                setHoverCells(null);
                audioManager.playSFX('fragment');
            } else {
                audioManager.playSFX('error');
            }
        } else if (step === 'SHADOWS') {
            audioManager.playSFX('click');
            if (shadowZones.includes(idx)) {
                setShadowZones(shadowZones.filter(i => i !== idx));
            } else {
                const isOccupied = Object.values(placedShapes).flat().includes(idx);
                if (!isOccupied && shadowZones.length < 3) {
                    setShadowZones([...shadowZones, idx]);
                }
            }
        }
    };

    const toggleRotation = (e: React.MouseEvent) => {
        e.preventDefault();
        audioManager.playSFX('click');
        setRotation(prev => {
            if (prev === 'H') return 'V';
            if (prev === 'V') return 'D1';
            if (prev === 'D1') return 'D2';
            return 'H';
        });
    };

    // --- NAVIGATION ---
    const canProceedToPlace = memoriesList.length === 3 && memoriesList.every(m => selectedMemories[m].trim().length > 0);
    const canProceedToShadows = Object.keys(placedShapes).length === 3;
    const canSubmit = step === 'SHADOWS' && shadowZones.length === 3;

    const handleSubmit = () => {
        const payload = memoriesList.map(questionText => ({
            prompt: formatTemplate(questionText), // [FIX] Send replaced formatted question to SERVER
            text: selectedMemories[questionText], // The content to show when revealed
            answer: selectedMemories[questionText], // The secret to guess
            cells: placedShapes[questionText]
        }));
        onSubmit(payload, shadowZones);
    };

    // --- RENDER HELPERS ---
    // Default Memories fallback
    const availableMemories = packMemories.length > 0 ? packMemories : [
        { text: "Quelle est ma plus grande peur ?" },
        { text: "Quel est mon péché mignon ?" },
        { text: "Pourquoi suis-je allergique aux chats ?" },
        { text: "Où ai-je sauté en parachute ?" },
        { text: "Pourquoi je déteste les lundis ?" },
        { text: "Où se trouve mon tatouage secret ?" },
        { text: "Quel talent inutile je maîtrise ?" },
        { text: "Dans quel pays ai-je vécu ?" },
        { text: "Que se passe-t-il quand je bois trop ?" },
        { text: "De quelle créature ai-je la phobie ?" }
    ];

    if (isWaiting) {
        return (
            <div className="h-screen flex flex-col items-center justify-center text-[var(--text-primary)] font-serif bg-[var(--bg-primary)]">
                <div className="relative">
                    <div className="w-16 h-16 border-4 border-[var(--accent-color)] rounded-full animate-spin border-t-transparent"></div>
                    <div className="absolute inset-0 flex items-center justify-center text-2xl">🧠</div>
                </div>
                <h2 className="text-2xl mt-6 font-bold text-[var(--accent-glow)] tracking-widest">SYNCHRONISATION</h2>
                <p className="text-[var(--text-secondary)] mt-2">En attente des autres joueurs pour débuter...</p>
            </div>
        );
    }

    return (
        <VintageLayout>
            <div className="min-h-screen flex flex-col items-center justify-center p-4">
                <motion.div
                    layout
                    className="max-w-6xl w-full bg-[var(--bg-primary)]/95 border border-[var(--glass-border)] rounded-xl p-8 shadow-2xl backdrop-blur-md flex gap-8 h-[85vh] relative overflow-hidden ring-1 ring-[#000]"
                >
                    {/* Background Grid Decoration */}
                    <div className="absolute inset-0 bg-grid-isometric opacity-10 pointer-events-none"></div>

                    {/* --- LEFT COLUMN: CONTEXT & INSTRUCTIONS --- */}
                    <div className="w-1/3 flex flex-col z-10">
                        <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[var(--accent-color)] to-[var(--accent-glow)] mb-2 uppercase tracking-tighter">
                            {step === 'SELECT' && "1. SÉLECTION"}
                            {step === 'PLACE' && "2. ANCRAGE (Souvenirs)"}
                            {step === 'SHADOWS' && "2. ANCRAGE (Défenses)"}
                        </h2>

                        <p className="text-[var(--text-secondary)] text-sm mb-6 leading-relaxed font-serif">
                            {step === 'SELECT' && "Choisissez 3 souvenirs fondateurs. Ce seront vos points d'ancrage dans le Cortex. Assignez-leur une réponse secrète."}
                            {step === 'PLACE' && "Ancrez vos souvenirs sur la grille. Sélectionnez un souvenir, puis CLIQUEZ sur la grille pour le poser."}
                            {step === 'SHADOWS' && "Sécurisez vos souvenirs avec 3 Zones d'Ombre. Si un adversaire tombe dessus, il subit un choc mnésique."}
                        </p>

                        {/* LISTE DES SOUVENIRS (Always visible for reference) */}
                        <div className="flex-1 overflow-y-auto space-y-3 pr-2 scrollbar-hide">
                            {step === 'SELECT' ? (
                                // SELECT MODE: List to choose
                                availableMemories.map((m, i) => {
                                    const isSel = m.text in selectedMemories;
                                    const response = (m as any).response || (m as any).defaultAnswer || "???";
                                    const size = getMemorySize(m.text);

                                    return (
                                        <div key={i} onClick={() => toggleMemorySelection(m.text, response)}
                                            className={`p-3 rounded cursor-pointer border transition-all ${isSel ? 'bg-[var(--accent-color)]/20 border-[var(--accent-glow)] text-[var(--accent-glow)]' : 'bg-[var(--glass-bg)] border-[var(--glass-border)] hover:bg-[var(--bg-secondary)] text-[var(--text-secondary)]'}`}>
                                            <div className="flex justify-between items-center">
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-bold font-serif">{formatTemplate(m.text)}</span>
                                                    <span className="text-[10px] uppercase tracking-wider opacity-70 mt-1">
                                                        Taille: {size} cases (Mots: {formatTemplate(m.text).split(/\s+/).length})
                                                    </span>
                                                </div>
                                                {isSel && <div className="text-[var(--accent-glow)] font-bold bg-[var(--accent-color)]/20 px-2 py-1 rounded">CHOISI</div>}
                                            </div>
                                        </div>
                                    )
                                })
                            ) : (
                                // PLACE/SHADOWS MODE: List of chosen memories to drag/click
                                memoriesList.map((mem, i) => {
                                    const isPlaced = !!placedShapes[mem];
                                    const isActive = currentMemoryToPlace === mem;
                                    const size = getMemorySize(mem);

                                    return (
                                        <div
                                            key={i}
                                            onClick={() => {
                                                if (step === 'PLACE') {
                                                    setCurrentMemoryToPlace(mem);
                                                    audioManager.playSFX('click');
                                                }
                                            }}
                                            className={`
                                                p-4 rounded border-l-4 transition-all cursor-pointer relative overflow-hidden group
                                                ${isActive ? 'bg-[var(--accent-color)]/20 border-[var(--accent-glow)] ring-1 ring-[var(--accent-glow)]/50' : 'bg-[var(--glass-bg)] border-[var(--border-color)]'}
                                                ${isPlaced ? 'opacity-50 grayscale' : 'opacity-100'}
                                                ${step !== 'PLACE' ? 'pointer-events-none' : ''}
                                            `}
                                        >
                                            <div className="text-xs text-[var(--text-secondary)] uppercase tracking-wider mb-1 flex justify-between">
                                                <span>SOUVENIR DE {size} CASES</span>
                                                {isPlaced && <span className="text-green-500 font-bold">ANCRÉ</span>}
                                            </div>
                                            <div className="text-sm font-bold text-[var(--text-primary)] font-serif">{formatTemplate(mem)}</div>
                                            {isActive && step === 'PLACE' && (
                                                <div className="absolute right-2 top-1/2 -translate-y-1/2 text-2xl animate-pulse">👉</div>
                                            )}
                                        </div>
                                    )
                                })
                            )}
                        </div>

                        {/* ACTION BUTTONS */}
                        <div className="mt-4 pt-4 border-t border-[var(--border-color)] space-y-3">
                            {/* ACCESSIBILITY: BIG ROTATE BUTTON */}
                            {step === 'PLACE' && (
                                <button
                                    onClick={(e) => toggleRotation(e)}
                                    className="w-full py-4 rounded-xl font-bold uppercase tracking-wider transition-all bg-[var(--glass-bg)] border-2 border-[var(--accent-glow)] text-[var(--accent-glow)] hover:bg-[var(--accent-color)]/20 flex items-center justify-center gap-2"
                                >
                                    <span className="text-2xl">🔄</span>
                                    <span>PIVOTER LA FORME ({rotation === 'H' ? 'HORIZ' : rotation === 'V' ? 'VERT' : 'DIAG'})</span>
                                </button>
                            )}

                            {step === 'SELECT' && (
                                <button disabled={!canProceedToPlace} onClick={() => { setStep('PLACE'); audioManager.playSFX('click'); }}
                                    className={`w-full py-3 rounded font-bold uppercase tracking-wider transition-all ${canProceedToPlace ? 'bg-[var(--accent-color)] hover:bg-[var(--accent-glow)] text-[var(--bg-primary)] shadow-lg' : 'bg-[var(--bg-secondary)] text-[var(--text-secondary)] cursor-not-allowed'}`}>
                                    Valider la Sélection ({memoriesList.length}/3)
                                </button>
                            )}
                            {step === 'PLACE' && (
                                <button disabled={!canProceedToShadows} onClick={() => { setStep('SHADOWS'); audioManager.playSFX('click'); }}
                                    className={`w-full py-3 rounded font-bold uppercase tracking-wider transition-all ${canProceedToShadows ? 'bg-[var(--accent-color)] hover:bg-[var(--accent-glow)] text-[var(--bg-primary)] shadow-lg' : 'bg-[var(--bg-secondary)] text-[var(--text-secondary)] cursor-not-allowed'}`}>
                                    Placer les Défenses
                                </button>
                            )}
                            {step === 'SHADOWS' && (
                                <button disabled={!canSubmit} onClick={() => { handleSubmit(); audioManager.playSFX('success'); }}
                                    className={`w-full py-3 rounded font-bold uppercase tracking-wider transition-all ${canSubmit ? 'bg-green-600 hover:bg-green-500 text-white shadow-lg' : 'bg-[var(--bg-secondary)] text-[var(--text-secondary)] cursor-not-allowed'}`}>
                                    Lancer la Partie
                                </button>
                            )}
                        </div>
                    </div>

                    {/* --- RIGHT COLUMN: THE GRID --- */}
                    <div className="flex-1 flex flex-col items-center justify-center bg-[var(--bg-board)] rounded-xl relative z-10 border border-[var(--border-color)] shadow-2xl">

                        {/* Grid Header Info */}
                        <div className="absolute top-4 w-full text-center pointer-events-none">
                            <span className="text-xs font-mono text-[var(--accent-glow)] opacity-60 uppercase tracking-[0.2em]">Cortex Neural Interface v3.5</span>
                        </div>

                        {/* ROTATION INDICATOR (Redundant but kept for visual feedback) */}
                        {step === 'PLACE' && (
                            <div className="absolute bottom-4 left-4 text-xs text-[var(--text-secondary)] flex items-center gap-2">
                                <span className="px-2 py-1 bg-[var(--bg-secondary)] rounded border border-[var(--border-color)]">CLIC DROIT</span>
                                <span>aussi disponible</span>
                            </div>
                        )}

                        <div
                            onContextMenu={toggleRotation}
                            className="bg-[#1a1614] p-2 rounded shadow-2xl border-2 border-[var(--border-color)] grid grid-cols-10 grid-rows-10 gap-1 w-[500px] h-[500px] relative"
                            style={{
                                backgroundImage: 'radial-gradient(circle at center, rgba(212, 175, 55, 0.05) 0%, transparent 70%)'
                            }}
                        >
                            {/* Grid Lines/Texture Overlay */}
                            <div className="absolute inset-0 pointer-events-none opacity-20"
                                style={{
                                    backgroundImage: 'linear-gradient(rgba(212, 175, 55, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(212, 175, 55, 0.1) 1px, transparent 1px)',
                                    backgroundSize: '10% 10%'
                                }}
                            />

                            {Array.from({ length: 100 }).map((_, i) => {
                                // Determine Cell State for Rendering
                                const isShadow = shadowZones.includes(i);
                                let isPlacedMemory = false;
                                let memoryColor = '';

                                // Check placed shapes
                                Object.entries(placedShapes).forEach(([mem, cells], idx) => {
                                    if (cells.includes(i)) {
                                        isPlacedMemory = true;
                                        // Colors: Use theme compatible colors
                                        const memIdx = memoriesList.indexOf(mem);
                                        // Gold, Purple, Blue - muted vintage tones (Red reserved for penalties)
                                        if (memIdx === 0) memoryColor = 'bg-[#d4af37] border-[#fcd34d]'; // Gold
                                        if (memIdx === 1) memoryColor = 'bg-[#6b21a8] border-[#a855f7]'; // Purple/Amethyst
                                        if (memIdx === 2) memoryColor = 'bg-[#1e3a8a] border-[#60a5fa]'; // Royal Blue
                                    }
                                });

                                // Check Hover
                                const isHover = hoverCells?.includes(i);
                                // NEW LOGIC: Invalid if colliding OR OOB (hoverValid === false)
                                const isCollision = isHover && (isCellOccupied(i, currentMemoryToPlace || undefined) || !hoverValid);

                                // Base Cell Style (Empty) - Increase visibility!
                                // Was: bg-[var(--bg-secondary)]/50 (Dark on Dark)
                                // New: bg-amber-900/20 (Light brown tint for empty slots)
                                let cellClass = 'bg-[#2a2522] border border-white/5 hover:border-white/20';

                                if (isShadow) {
                                    // Shadows = Traps = Danger = RED
                                    cellClass = 'bg-[#450a0a] shadow-[inset_0_0_10px_rgba(220,38,38,0.8)] border border-red-600/60';
                                    // Optional: Add a pattern or icon overlay in CSS if possible, but color distinction is key first
                                } else if (isPlacedMemory) {
                                    cellClass = `${memoryColor} shadow-lg scale-95 rounded-sm border-2 z-10 opacity-90`;
                                } else if (step === 'PLACE' && isHover) {
                                    // Hover Logic
                                    if (isCollision) {
                                        cellClass = 'bg-red-500/60 animate-pulse border-2 border-red-400 z-20';
                                    } else {
                                        // Valid placement preview
                                        cellClass = 'bg-[var(--accent-color)]/60 border-2 border-[var(--accent-glow)] shadow-[0_0_15px_rgba(212,175,55,0.4)] z-20';
                                    }
                                } else if (step === 'PLACE' && currentMemoryToPlace && !isPlacedMemory && !isShadow) {
                                    // Hint mode: Slightly highlight valid empty cells to show "Active Grid"
                                    cellClass += ' hover:bg-[#3f3a36] cursor-crosshair';
                                }

                                return (
                                    <div
                                        key={i}
                                        onMouseEnter={() => step === 'PLACE' && handleGridHover(i)}
                                        onClick={() => handleGridClick(i)}
                                        className={`rounded-[2px] transition-all duration-75 ${cellClass}`}
                                    />
                                );
                            })}
                        </div>
                    </div>

                </motion.div>
            </div>
        </VintageLayout>
    );
};

export default SetupMemories;
